import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Save, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { defaultSettings } from '@/hooks/useGradeSettings';
import ProductGradeBadge from '@/components/ProductGradeBadge';

interface GradeSettings {
  grade: 'A' | 'B' | 'C' | 'D';
  label: string;
  // This internal description is for the Admin UI card header (static)
  uiDescription: string;
  defaultDiscount: number;
  color: string;
  // This is the editable description text
  descriptionText: string;
}

const defaultGradeSettings: GradeSettings[] = [
  {
    grade: 'A',
    label: 'פרימיום',
    uiDescription: 'מוצר איכותי במצב מושלם',
    defaultDiscount: 30,
    color: 'emerald',
    descriptionText: defaultSettings.find(s => s.grade === 'A')?.description || ''
  },
  {
    grade: 'B',
    label: 'סטנדרט',
    uiDescription: 'מוצר במצב טוב',
    defaultDiscount: 45,
    color: 'blue',
    descriptionText: defaultSettings.find(s => s.grade === 'B')?.description || ''
  },
  {
    grade: 'C',
    label: 'כפי שהוא',
    uiDescription: 'נמכר במצבו, ללא החזרה',
    defaultDiscount: 70,
    color: 'orange',
    descriptionText: defaultSettings.find(s => s.grade === 'C')?.description || ''
  },
  {
    grade: 'D',
    label: 'מכירה סופית',
    uiDescription: 'מכירה סופית, ללא החזרה',
    defaultDiscount: 85,
    color: 'red',
    descriptionText: defaultSettings.find(s => s.grade === 'D')?.description || ''
  }
];

const GradeManagementModule = () => {
  const [gradeSettings, setGradeSettings] = useState<GradeSettings[]>(defaultGradeSettings);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [products, setProducts] = useState<any[]>([]);

  useEffect(() => {
    loadSettings();
    loadProducts();
  }, []);

  const loadSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('site_settings')
        .select('key, value')
        .like('key', 'grade_%');

      if (error) throw error;

      if (data && data.length > 0) {
        const updatedSettings = gradeSettings.map(setting => {
          const discountVal = data.find(d => d.key === `grade_${setting.grade}_discount`)?.value;
          const descriptionVal = data.find(d => d.key === `grade_${setting.grade}_description`)?.value;

          return {
            ...setting,
            defaultDiscount: discountVal ? parseFloat(discountVal) : setting.defaultDiscount,
            descriptionText: descriptionVal !== undefined ? descriptionVal : setting.descriptionText,
          };
        });
        setGradeSettings(updatedSettings);
      }
    } catch (error) {
      console.error('Error loading grade settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadProducts = async () => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('id, name, grade, price_per_unit, original_price')
        .eq('is_active', true);

      if (error) throw error;
      setProducts(data || []);
    } catch (error) {
      console.error('Error loading products:', error);
    }
  };

  const handleSettingChange = (grade: string, field: keyof GradeSettings, value: any) => {
    setGradeSettings(prev =>
      prev.map(setting =>
        setting.grade === grade
          ? { ...setting, [field]: value }
          : setting
      )
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updates = [];

      gradeSettings.forEach(setting => {
        updates.push({
          key: `grade_${setting.grade}_discount`,
          value: setting.defaultDiscount.toString()
        });
        updates.push({
          key: `grade_${setting.grade}_description`,
          value: setting.descriptionText
        });
      });

      const promises = updates.map(update =>
        supabase
          .from('site_settings')
          .upsert(update, { onConflict: 'key' })
      );

      const results = await Promise.all(promises);
      const errors = results.filter(r => r.error);

      if (errors.length > 0) {
        throw new Error('Failed to save some settings');
      }

      toast.success('הגדרות דירוג נשמרו בהצלחה');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('שגיאה בשמירת הגדרות');
    } finally {
      setSaving(false);
    }
  };

  const getProductCountByGrade = (grade: string) => {
    return products.filter(p => (p.grade || 'B') === grade).length;
  };

  const calculateAverageDiscount = (grade: string) => {
    const gradeProducts = products.filter(p => (p.grade || 'B') === grade);
    if (gradeProducts.length === 0) return 0;

    const totalDiscount = gradeProducts.reduce((sum, p) => {
      if (!p.original_price || !p.price_per_unit) return sum;
      const discount = ((p.original_price - p.price_per_unit) / p.original_price) * 100;
      return sum + discount;
    }, 0);

    return (totalDiscount / gradeProducts.length).toFixed(1);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">טוען...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" dir="rtl">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">ניהול דירוגי מוצרים</h2>
          <p className="text-muted-foreground mt-1">
            הגדר את אחוזי ההנחה, תיאורי הדירוג ומדיניות ההחזרות
          </p>
        </div>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? (
            <>
              <RefreshCw className="ml-2 h-4 w-4 animate-spin" />
              שומר...
            </>
          ) : (
            <>
              <Save className="ml-2 h-4 w-4" />
              שמור שינויים
            </>
          )}
        </Button>
      </div>

      {/* Grade Settings Grid */}
      <div className="grid grid-cols-1 gap-6">
        {gradeSettings.map((setting) => (
          <Card key={setting.grade}>
            <CardHeader>
              <div className="flex justify-between items-start">
                <div className="space-y-1">
                  <CardTitle className="flex items-center gap-2">
                    <ProductGradeBadge grade={setting.grade} size="lg" />
                  </CardTitle>
                  <CardDescription>{setting.uiDescription}</CardDescription>
                </div>
                <Badge variant="secondary" className="text-lg font-bold">
                  {getProductCountByGrade(setting.grade)} מוצרים
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Discount Setting */}
                <div className="space-y-2">
                  <Label htmlFor={`discount-${setting.grade}`}>
                    אחוז הנחה מומלץ (%)
                  </Label>
                  <Input
                    id={`discount-${setting.grade}`}
                    type="number"
                    min="0"
                    max="100"
                    value={setting.defaultDiscount}
                    onChange={(e) => handleSettingChange(setting.grade, 'defaultDiscount', parseFloat(e.target.value))}
                    className="text-lg font-semibold"
                  />
                  <p className="text-sm text-muted-foreground">
                    ממוצע הנחה בפועל: {calculateAverageDiscount(setting.grade)}%
                  </p>
                </div>

                {/* Description Field - Moved to take up the right column matching previous layout logic but clearer */}
                <div className="space-y-2">
                  <Label htmlFor={`description-${setting.grade}`}>
                    תיאור הדירוג (יופיע בכל מקום באתר)
                  </Label>
                  <Textarea
                    id={`description-${setting.grade}`}
                    value={setting.descriptionText}
                    onChange={(e) => handleSettingChange(setting.grade, 'descriptionText', e.target.value)}
                    placeholder="הזן תיאור לדירוג זה..."
                    rows={4}
                    className="resize-none"
                  />
                  <p className="text-xs text-muted-foreground">
                    טקסט זה יופיע ב"מדריך דירוגי איכות" בעמוד המוצר ובעמוד אודות/מדיניות.
                  </p>
                </div>
              </div>

            </CardContent>
          </Card>
        ))}
      </div>

      {/* Info Card */}
      <Card className="bg-muted/30">
        <CardHeader>
          <CardTitle className="text-lg">הערות חשובות</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-muted-foreground">
          <p>• אחוזי ההנחה המוצגים כאן הם המלצה בלבד - כל מוצר יכול להיות במחיר שונה</p>
          <p>• התיאורים שתוסיפו כאן יופיעו באופן אוטומטי בכל המוצרים המשויכים לדירוג זה</p>
          <p>• השינויים ייכנסו לתוקף מיד לאחר השמירה</p>
        </CardContent>
      </Card>
    </div>
  );
};

export default GradeManagementModule;
